import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Stack, Alert, CircularProgress, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, Switch, FormControlLabel,
  TextField, Divider,
} from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { bulkOutreachContacts, getInteractionTemplates } from '../services/api';

// Strip paging keys; what's left is the filter set shared with the bulk endpoint body.
const filterBody = (params) => {
  const { page, pageSize, ...rest } = params || {};
  return rest;
};

const SKIP_LABELS = {
  no_template_for_group: 'No template chosen for group',
  budget_exhausted: 'Over daily send limit',
};

export default function BulkOutreachDialog({ open, onClose, filters, onQueued }) {
  const [templates, setTemplates] = useState([]);
  const [preview, setPreview] = useState(null);     // dry-run result
  const [mapping, setMapping] = useState({});        // industryGroupId -> templateId
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduledFor, setScheduledFor] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);        // post-send result

  const body = useMemo(() => filterBody(filters), [filters]);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const [tplRes, prevRes] = await Promise.all([
        getInteractionTemplates(),
        bulkOutreachContacts({ ...body, dryRun: true, groupTemplates: [] }),
      ]);
      const tpls = (tplRes.data || []).filter(
        (t) => t.isActive && (t.channel === 'Email' || t.channel === 0),
      );
      setTemplates(tpls);
      setPreview(prevRes.data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [body]);

  useEffect(() => {
    if (open) {
      setMapping({});
      setScheduleOn(false);
      setScheduledFor('');
      loadPreview();
    }
  }, [open, loadPreview]);

  // Groups that can actually be targeted (have a real industry group id).
  const groups = (preview?.perGroup || []).filter((g) => g.industryGroupId != null);
  const ungrouped = (preview?.perGroup || []).find((g) => g.industryGroupId == null);

  const groupTemplates = Object.entries(mapping)
    .filter(([, tid]) => tid)
    .map(([gid, tid]) => ({ industryGroupId: Number(gid), templateId: Number(tid) }));

  const willQueue = groups
    .filter((g) => mapping[g.industryGroupId])
    .reduce((sum, g) => sum + g.matched, 0);

  const send = async () => {
    setSending(true);
    setError('');
    try {
      const payload = { ...body, dryRun: false, groupTemplates };
      if (scheduleOn && scheduledFor) {
        payload.scheduledFor = new Date(scheduledFor).toISOString();
      }
      const { data } = await bulkOutreachContacts(payload);
      setResult(data);
      onQueued?.(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to queue emails');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Email filtered contacts</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress /></Box>
        ) : result ? (
          <Stack spacing={2}>
            <Alert severity="success">
              Queued <strong>{result.queued}</strong> email{result.queued === 1 ? '' : 's'}
              {scheduleOn && scheduledFor ? ' (scheduled)' : ' — sending shortly, respects daily limit'}.
            </Alert>
            {Object.keys(result.skipped || {}).length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Skipped</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {Object.entries(result.skipped).map(([reason, count]) => (
                    <Chip key={reason} size="small"
                      label={`${SKIP_LABELS[reason] || reason}: ${count}`} />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Typography variant="body2">
                Sendable matched: <strong>{preview?.matched ?? 0}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Daily budget remaining: <strong>{preview?.budgetRemaining ?? 0}</strong>
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Suppressed / no-email / unsubscribed contacts are already excluded. Pick a template
              per industry group; groups left blank are skipped.
            </Typography>

            {groups.length === 0 ? (
              <Alert severity="info">No sendable contacts with an industry group match the current filters.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Industry group</TableCell>
                    <TableCell align="right">Contacts</TableCell>
                    <TableCell>Template</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map((g) => (
                    <TableRow key={g.industryGroupId}>
                      <TableCell>{g.industryGroupName}</TableCell>
                      <TableCell align="right">{g.matched}</TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            displayEmpty
                            value={mapping[g.industryGroupId] || ''}
                            onChange={(e) => setMapping((m) => ({ ...m, [g.industryGroupId]: e.target.value }))}
                          >
                            <MenuItem value=""><em>— skip —</em></MenuItem>
                            {templates.map((t) => (
                              <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {ungrouped && (
              <Typography variant="caption" color="text.secondary">
                {ungrouped.matched} matched contact{ungrouped.matched === 1 ? '' : 's'} have no industry
                group and will be skipped.
              </Typography>
            )}

            <Divider />
            <FormControlLabel
              control={<Switch checked={scheduleOn} onChange={(e) => setScheduleOn(e.target.checked)} />}
              label="Schedule for later (otherwise queued to send now)"
            />
            {scheduleOn && (
              <TextField
                type="datetime-local"
                size="small"
                label="Send at"
                InputLabelProps={{ shrink: true }}
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
        {!result && (
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={16} /> : <SendOutlinedIcon />}
            disabled={loading || sending || groupTemplates.length === 0 || willQueue === 0
              || (scheduleOn && !scheduledFor)}
            onClick={send}
          >
            {willQueue > 0 ? `Queue ${willQueue} email${willQueue === 1 ? '' : 's'}` : 'Queue emails'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
